import { useContext, useMemo, useState, useRef, useEffect } from 'react';
import styles from './CompositionRowValue.module.css';
import ThemeContext from 'config/context/themeContext';
import dataTypesConfig from '../../constants/dataTypes.json';
import { typeConverters } from '../../utils/typeConverters';

const CompositionRowValue = ({ value, type }) => {
    const { dark } = useContext(ThemeContext);
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    
    // Get converter for current type
    const converter = useMemo(() => 
        typeConverters[type] || typeConverters[dataTypesConfig.supportedTypes.STRING], 
        [type]
    );

    // Handle boolean type specially
    if (type === 'bool') {
        return (
            <div className={`${styles.boolean_value} ${value ? styles.boolean_value_true : ''} ${dark ? styles.dark : ''}`}></div>
        );
    }

    // Handle time_point type with custom formatting
    if (type === dataTypesConfig.supportedTypes.TIME_POINT) {
        const displayValue = converter.toDisplay(value);
        if (displayValue) {
            try {
                const date = new Date(displayValue);
                if (!isNaN(date.getTime())) {
                    const day = date.getDate().toString().padStart(2, '0');
                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                    const year = date.getFullYear();
                    const hours = date.getHours().toString().padStart(2, '0');
                    const minutes = date.getMinutes().toString().padStart(2, '0');
                    
                    return (
                        <span className={`${styles.value} ${dark ? styles.dark : ''}`}>
                            {`${day}.${month}.${year}. ${hours}:${minutes}`}
                        </span>
                    );
                }
            } catch (error) {
                console.warn('Error formatting time_point:', error);
            }
        }
    }

    // Object/Array preview (for nested values)
    if (value !== null && typeof value === 'object') {
        const isArrayValue = Array.isArray(value);
        const summary = isArrayValue ? `[${value.length}]` : '{…}';
        const pretty = (() => {
            try { return JSON.stringify(value, null, 2); } catch { return '{}'; }
        })();

        return (
            <div className={`${styles.value_object} ${dark ? styles.dark : ''}`}>
                <button type="button" className={styles.json_trigger} onClick={() => setOpen(!open)}>
                    {isArrayValue ? 'View list' : 'View object'} {summary}
                </button>
                {open && (
                    <div className={styles.json_popover} ref={popoverRef}>
                        <pre className={styles.json_pre}>{pretty}</pre>
                        <div className={styles.json_actions}>
                            <button className={styles.ghost} type="button" onClick={() => setOpen(false)}>Close</button>
                            <button className={styles.primary} type="button" onClick={async () => {
                                try { await navigator.clipboard.writeText(pretty); } catch {}
                            }}>Copy</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // For all other types, use converter to display
    const displayValue = converter.toDisplay(value);
    return (
        <span className={`${styles.value} ${dark ? styles.dark : ''}`}>
            {displayValue}
        </span>
    );
}

export default CompositionRowValue;